import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const sanitizeNext = (raw) =>
  raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '';

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = sanitizeNext(searchParams.get('next'));
  const postProfileDest = nextPath || '/account';
  const { user, loading, setUser, checkAuth } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from the provider (Google etc.) when present, so the user just
  // confirms or edits rather than retyping their name from scratch.
  useEffect(() => {
    if (user && !displayName && user.user_display_name) {
      setDisplayName(user.user_display_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Gate: must be authenticated; if profile is already complete, skip this page.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.profile_complete) {
      navigate(postProfileDest, { replace: true });
    }
  }, [user, loading, navigate, postProfileDest]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = displayName.trim();
    if (trimmed.length < 2 || trimmed.length > 50) {
      setError('Display name must be between 2 and 50 characters.');
      return;
    }
    if (!/[A-Za-z0-9]/.test(trimmed)) {
      setError('Display name must contain at least one letter or number.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE}/auth/complete-profile`,
        { display_name: trimmed },
        { withCredentials: true }
      );
      setUser(response.data.user);
      await checkAuth();
      navigate(postProfileDest, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || user.profile_complete) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full" data-testid="complete-profile-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <UserIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">One last step</h1>
          <p className="text-gray-600">
            Choose how your name appears on your photographer profile.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="display-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Display name
              </label>
              <Input
                id="display-name"
                data-testid="display-name-input"
                type="text"
                autoFocus
                required
                maxLength={50}
                placeholder="e.g. John Smith"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="h-12 text-base"
              />
              <p className="text-xs text-gray-500 mt-2">
                This is also used in your public URL — for example,{' '}
                <code className="text-gray-700">
                  /photos/photographer/{user.user_id}-{
                    (displayName.trim() || 'yourname')
                      .normalize('NFKD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '') || 'yourname'
                  }
                </code>
              </p>
            </div>

            {error ? (
              <p className="text-sm text-red-600" data-testid="complete-profile-error">{error}</p>
            ) : null}

            <Button
              type="submit"
              data-testid="complete-profile-submit"
              disabled={submitting}
              className="w-full h-12 text-base font-medium bg-gray-900 text-white hover:bg-gray-800 flex items-center justify-center space-x-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving…</span>
                </>
              ) : (
                <span>Continue</span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
